@if (chat.open()) {
  <aside
    class="agent-panel al-glass"
    [class.agent-panel--mobile]="mobile"
    [style.top.px]="mobile ? null : top"
    [style.width.px]="mobile ? null : chat.width()"
    role="complementary"
    [attr.aria-label]="'agentPanel.title' | translate"
  >
    @if (!mobile) {
      <div
        class="agent-panel__resize"
        role="separator"
        aria-orientation="vertical"
        [attr.aria-label]="'agentPanel.resize' | translate"
        (pointerdown)="startResize($event)"
        (dblclick)="chat.resetWidth()"
      ></div>
    }

    <div class="al-glass__body agent-panel__body">
      <header class="agent-panel__head">
        <button
          type="button"
          class="agent-panel__title"
          (click)="historyOpen = !historyOpen; historyOpen && chat.loadThreads()"
          [attr.aria-expanded]="historyOpen"
          [nbTooltip]="'agentPanel.history' | translate"
        >
          <nb-icon icon="orbi" pack="al" class="agent-panel__orbi"></nb-icon>
          <span class="agent-panel__title-text">{{ title() || ('agentPanel.title' | translate) }}</span>
          <nb-icon icon="chevron-down-outline" class="agent-panel__chevron" [class.agent-panel__chevron--open]="historyOpen"></nb-icon>
        </button>
        <button
          nbButton
          ghost
          size="small"
          type="button"
          (click)="newChat()"
          [nbTooltip]="'agentPanel.newChat' | translate"
          [attr.aria-label]="'agentPanel.newChat' | translate"
        >
          <nb-icon icon="plus-outline"></nb-icon>
        </button>
        <button
          nbButton
          ghost
          size="small"
          type="button"
          (click)="chat.close()"
          [nbTooltip]="'agentPanel.close' | translate"
          [attr.aria-label]="'agentPanel.close' | translate"
        >
          <nb-icon icon="close-outline"></nb-icon>
        </button>
      </header>

      @if (historyOpen) {
        <nav class="agent-panel__history" [attr.aria-label]="'agentPanel.history' | translate">
          @for (group of groups(); track group.key) {
            <p class="agent-panel__history-group">{{ 'agentPanel.groups.' + group.key | translate }}</p>
            @for (thread of group.threads; track thread.id) {
              <div class="agent-panel__history-row" [class.agent-panel__history-row--active]="thread.id === chat.threadId()">
                <button type="button" class="agent-panel__history-pick" (click)="pick(thread)">
                  {{ thread.title || ('agentPanel.untitled' | translate) }}
                </button>
                <button
                  type="button"
                  class="agent-panel__history-delete"
                  (click)="remove(thread, $event)"
                  [nbTooltip]="'agentPanel.delete' | translate"
                  [attr.aria-label]="'agentPanel.delete' | translate"
                >
                  <nb-icon icon="trash-2-outline"></nb-icon>
                </button>
              </div>
            }
          } @empty {
            <p class="agent-panel__muted">{{ 'agentPanel.historyEmpty' | translate }}</p>
          }
        </nav>
      }

      <div class="agent-panel__messages" #scroller aria-live="polite">
        <!-- Changes the person's own agent (Claude, Cursor … over MCP) proposed: only they approve, here. -->
        @if (approvals.items().length) {
          <section class="agent-external" [attr.aria-label]="'agentPanel.external.title' | translate">
            <p class="agent-external__title">{{ 'agentPanel.external.title' | translate }}</p>
            @for (item of approvals.items(); track item.id) {
              <section class="agent-approval">
                <p class="agent-approval__title">{{ 'agentPanel.external.from' | translate: { name: item.connection || '' } }}</p>
                @if (item.preview?.reason) {
                  <p class="agent-msg__text">{{ item.preview?.reason }}</p>
                }
                <div class="agent-approval__action">
                  <p class="agent-approval__label">{{ actionLabel({ action_id: item.action_id, summary: item.preview?.summary }) }}</p>
                  <p class="agent-approval__risk">{{ 'agentPanel.approval.risk.' + (item.risk || 'R3') | translate }}</p>
                  @if (item.preview?.fields?.length) {
                    <dl class="agent-approval__fields">
                      @for (f of item.preview?.fields; track $index) {
                        <dt>{{ fieldLabel(f) }}</dt>
                        <dd>{{ fieldValue(f) }}</dd>
                      }
                    </dl>
                  }
                  @if (item.preview?.before && !fieldsDescribed(item.preview?.fields)) {
                    <details class="agent-approval__before">
                      <summary>{{ 'agentPanel.approval.before' | translate }}</summary>
                      <pre>{{ item.preview?.before | json }}</pre>
                    </details>
                  }
                </div>
                @switch (item.state) {
                  @case ('pending') {
                    <div class="agent-approval__buttons">
                      <button nbButton size="small" status="danger" appearance="outline" type="button" (click)="approvals.decide(item.id, false)">
                        {{ 'agentPanel.approval.reject' | translate }}
                      </button>
                      <button nbButton size="small" status="primary" type="button" (click)="approvals.decide(item.id, true)">
                        {{ 'agentPanel.approval.run' | translate }}
                      </button>
                    </div>
                  }
                  @case ('sending') {
                    <p class="agent-panel__progress"><nb-icon icon="loader-outline" class="al-spin"></nb-icon>{{ 'agentPanel.approval.sending' | translate }}</p>
                  }
                  @default {
                    <p class="agent-approval__state">{{ 'agentPanel.external.' + item.state | translate }}</p>
                  }
                }
              </section>
            }
          </section>
        }
        @if (chat.loadingThread()) {
          <div class="agent-panel__center"><app-orbi-loader [size]="72"></app-orbi-loader></div>
        } @else if (!chat.messages().length) {
          <div class="agent-panel__empty">
            <nb-icon icon="orbi" pack="al" class="agent-panel__hero"></nb-icon>
            <p class="agent-panel__greeting">{{ 'agentPanel.greetingTitle' | translate }}</p>
            <p class="agent-panel__muted">{{ 'agentPanel.greetingText' | translate }}</p>
            <div class="agent-panel__starters">
              @for (key of starters; track key) {
                <button nbButton size="small" status="basic" type="button" (click)="starter(key)">
                  {{ 'agentPanel.starters.' + key | translate }}
                </button>
              }
            </div>
          </div>
        }

        @for (message of chat.messages(); track message.id) {
          @if (message.approval; as approval) {
            <section class="agent-approval" [attr.aria-label]="'agentPanel.approval.title' | translate">
              <p class="agent-approval__title">{{ 'agentPanel.approval.title' | translate }}</p>
              @if (approval.reason) {
                <div class="agent-msg__text" [innerHTML]="approval.reason | agentMarkdown"></div>
              }
              @for (action of approval.actions; track action.proposal_id) {
                <div class="agent-approval__action">
                  <p class="agent-approval__label">{{ actionLabel(action) }}</p>
                  <p class="agent-approval__risk">{{ 'agentPanel.approval.risk.' + (action.risk || 'R3') | translate }}</p>
                  @if (action.fields?.length) {
                    <dl class="agent-approval__fields">
                      @for (f of action.fields; track $index) {
                        <dt>{{ fieldLabel(f) }}</dt>
                        <dd>{{ fieldValue(f) }}</dd>
                      }
                    </dl>
                  }
                  @if (action.before && !fieldsDescribed(action.fields)) {
                    <details class="agent-approval__before">
                      <summary>{{ 'agentPanel.approval.before' | translate }}</summary>
                      <pre>{{ action.before | json }}</pre>
                    </details>
                  }
                  @if (action.ui_target) {
                    <button type="button" class="agent-link" (click)="chat.show(action.ui_target, action.ui_anchor)">
                      {{ 'agentPanel.trace.show' | translate }}
                    </button>
                  }
                </div>
              }
              @switch (approval.state) {
                @case ('pending') {
                  <div class="agent-approval__buttons">
                    <button nbButton size="small" status="danger" appearance="outline" type="button" (click)="chat.decide(message.id, false)">
                      {{ 'agentPanel.approval.reject' | translate }}
                    </button>
                    <button nbButton size="small" status="primary" type="button" (click)="chat.decide(message.id, true)">
                      {{ 'agentPanel.approval.run' | translate }}
                    </button>
                  </div>
                }
                @case ('sending') {
                  <p class="agent-panel__progress"><nb-icon icon="loader-outline" class="al-spin"></nb-icon>{{ 'agentPanel.approval.sending' | translate }}</p>
                }
                @default {
                  <p class="agent-approval__state">{{ 'agentPanel.approval.' + approval.state | translate }}</p>
                }
              }
            </section>
          } @else {
          @if (message.trace; as trace) {
            <p
              class="agent-trace"
              [class.agent-trace--failed]="!trace.ok && trace.reason !== 'rejected_by_user'"
              [class.agent-trace--rejected]="trace.reason === 'rejected_by_user'"
            >
              <nb-icon [icon]="trace.ok ? 'checkmark-outline' : trace.reason === 'rejected_by_user' ? 'slash-outline' : 'alert-circle-outline'"></nb-icon>
              <span>{{ traceLabel(trace) }}</span>
              @if (trace.ok && trace.uiTarget) {
                <button type="button" class="agent-link" (click)="chat.show(trace.uiTarget, trace.uiAnchor)">{{ 'agentPanel.trace.show' | translate }}</button>
              }
            </p>
          } @else {
          <div class="agent-msg" [class.agent-msg--user]="message.role === 'user'">
            @if (message.role === 'agent') {
              <div class="agent-msg__text" [innerHTML]="message.text | agentMarkdown"></div>
              @if (message.streaming) {
                <app-orbi-text-dot [size]="16" class="agent-msg__writing"></app-orbi-text-dot>
              } @else if (message.text && !message.error) {
                <button
                  nbButton
                  ghost
                  size="tiny"
                  type="button"
                  class="agent-msg__speak"
                  [class.agent-msg__speak--on]="voice.playingId() === message.id"
                  [nbSpinner]="voice.loadingId() === message.id"
                  nbSpinnerSize="tiny"
                  [nbTooltip]="(voice.playingId() === message.id ? 'agentPanel.speakStop' : 'agentPanel.speak') | translate"
                  [attr.aria-label]="(voice.playingId() === message.id ? 'agentPanel.speakStop' : 'agentPanel.speak') | translate"
                  (click)="voice.toggle(message.id, message.text)"
                >
                  <nb-icon [icon]="voice.playingId() === message.id ? 'stop-circle-outline' : 'volume-up-outline'"></nb-icon>
                </button>
              }
            } @else {
              <div class="agent-msg__text">{{ message.text }}</div>
            }
          </div>
          }
          }
        }

        @if (chat.progress(); as label) {
          <p class="agent-panel__progress"><nb-icon icon="loader-outline" class="al-spin"></nb-icon>{{ label }}</p>
        }
        @if (chat.error(); as error) {
          <p class="agent-panel__error" role="alert">{{ 'agentPanel.errors.' + error | translate }}</p>
        }
      </div>

      <form class="agent-panel__composer" alAnchor="agent.panel-input" (submit)="$event.preventDefault(); send()">
        <textarea
          #input
          nbInput
          fullWidth
          rows="1"
          name="draft"
          data-enter-ignore
          [(ngModel)]="draft"
          (input)="autosize()"
          (keydown)="onKeydown($event)"
          [placeholder]="(listening ? 'agentPanel.listening' : 'agentPanel.placeholder') | translate"
          [attr.aria-label]="'agentPanel.placeholder' | translate"
        ></textarea>
        @if (speechSupported) {
          <button
            nbButton
            ghost
            type="button"
            class="agent-panel__mic"
            [class.agent-panel__mic--on]="listening"
            [attr.aria-pressed]="listening"
            [nbTooltip]="(listening ? 'agentPanel.dictateStop' : 'agentPanel.dictate') | translate"
            [attr.aria-label]="(listening ? 'agentPanel.dictateStop' : 'agentPanel.dictate') | translate"
            (click)="toggleDictation()"
          >
            <nb-icon [icon]="listening ? 'stop-circle-outline' : 'mic-outline'"></nb-icon>
          </button>
        }
        <button
          nbButton
          status="primary"
          type="submit"
          class="agent-panel__send"
          [disabled]="!draft.trim() || chat.busy()"
          [attr.aria-label]="'agentPanel.send' | translate"
        >
          <nb-icon icon="paper-plane-outline"></nb-icon>
        </button>
      </form>
    </div>
  </aside>
}
